import { useState, useEffect, useCallback, useRef } from "react";
import type { ReactElement } from "react";
import { API_BASE_URL } from "../../../../config";
import { getAuthHeaders } from "../../../../api/auth";
import "./PlanningOverviewPage.css";

interface TimeSlotCapacity {
    total: number;
    used: number;
}

interface TimeSlotData {
    id: string | null;
    isActive: boolean;
    capacities: Record<string, TimeSlotCapacity>;
}

interface PlanningOverview {
    calendarWeek: number;
    year: number;
    overview: Record<string, Record<string, TimeSlotData>>;
}

// Props interface - currentUser nicht mehr benötigt, da tenantId aus JWT Cookie kommt
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface PlanningOverviewPageProps { }

const DAYS = ["MO", "DI", "MI", "DO", "FR", "SA"];
const TIME_SLOTS = ["06-08", "09-11", "12-14", "15-17", "18-21"];

// Werk-spezifische Fraktionen werden vom Backend geladen
const PLANTS = [
    { id: "0123", name: "Werk 0123" },
    { id: "0128", name: "Werk 0128" },
];

function getCurrentWeek(): number {
    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

export function PlanningOverviewPage(_props: PlanningOverviewPageProps): ReactElement {
    const [planningData, setPlanningData] = useState<PlanningOverview | null>(null);
    const [selectedWeek, setSelectedWeek] = useState<number>(getCurrentWeek());
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedPlant, setSelectedPlant] = useState<string>(() => {
        // Lade aus LocalStorage oder nutze Default
        return localStorage.getItem("selectedPlant") || "0123";
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);

    // Lokaler State für Input-Werte (optimistisches Update)
    const [localCapacities, setLocalCapacities] = useState<Record<string, number>>({});

    // Debounce timer für Kapazitäts-Updates
    const updateTimerRef = useRef<Record<string, NodeJS.Timeout>>({});

    const fetchPlanningData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/planning/overview?calendarWeek=${selectedWeek}&year=${selectedYear}&plant=${selectedPlant}`,
                { headers: getAuthHeaders(), credentials: "include" }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            setPlanningData(data);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Fehler beim Laden der Daten";
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [selectedWeek, selectedYear, selectedPlant]);

    useEffect(() => {
        void fetchPlanningData();
    }, [fetchPlanningData]);

    const handlePreviousWeek = useCallback(() => {
        if (selectedWeek === 1) {
            setSelectedWeek(52);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedWeek(selectedWeek - 1);
        }
    }, [selectedWeek, selectedYear]);

    const handleNextWeek = useCallback(() => {
        if (selectedWeek === 52) {
            setSelectedWeek(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedWeek(selectedWeek + 1);
        }
    }, [selectedWeek, selectedYear]);

    const handlePlantChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPlant = event.target.value;
        setSelectedPlant(newPlant);
        localStorage.setItem("selectedPlant", newPlant);
        // Data will reload automatically via useEffect dependency on selectedPlant
    }, []);

    const handlePrint = useCallback(() => {
        // CSS für Querformat-Druck hinzufügen
        const style = document.createElement("style");
        style.textContent = `
            @media print {
                @page {
                    size: landscape;
                    margin: 5mm;
                }
                
                /* Scrollleisten entfernen */
                html, body {
                    overflow: visible !important;
                    height: auto !important;
                    width: auto !important;
                }
                
                /* Zeige nur die Planungsübersicht */
                body * {
                    visibility: hidden;
                }
                
                .planning-overview,
                .planning-overview * {
                    visibility: visible;
                }
                
                .planning-overview {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    height: auto !important;
                    overflow: visible !important;
                    padding: 0;
                    background: white;
                    transform: scale(0.85);
                    transform-origin: top left;
                }
                
                /* Verstecke Bedienelemente */
                .week-selector button,
                .plant-select,
                .print-btn,
                .refresh-btn {
                    display: none !important;
                }
                
                .save-notification {
                    display: none !important;
                }
                
                /* Header kompakter */
                .planning-header {
                    margin-bottom: 5px;
                    padding: 5px;
                    page-break-after: avoid;
                }
                
                .planning-header h1 {
                    font-size: 14px;
                    text-align: center;
                    margin: 0;
                }
                
                .week-selector {
                    justify-content: center;
                }
                
                .week-selector span {
                    font-size: 14px;
                    font-weight: bold;
                }
                
                /* Grid stark komprimiert */
                .planning-grid {
                    width: 100%;
                    font-size: 7px;
                    overflow: visible !important;
                    height: auto !important;
                }
                
                .grid-header {
                    font-size: 8px;
                }
                
                .header-cell {
                    padding: 2px;
                }
                
                .grid-row {
                    page-break-inside: avoid;
                }
                
                .time-slot-label {
                    font-size: 7px;
                    padding: 2px;
                }
                
                /* Zellen maximal komprimiert */
                .time-slot-cell {
                    padding: 2px;
                    font-size: 7px;
                    overflow: visible !important;
                }
                
                .slot-header {
                    margin-bottom: 2px;
                }
                
                .slot-total {
                    font-size: 7px;
                    font-weight: bold;
                }
                
                .toggle-btn {
                    font-size: 8px;
                    padding: 0;
                    width: 14px;
                    height: 14px;
                }
                
                .capacity-list {
                    overflow: visible !important;
                    max-height: none !important;
                }
                
                .capacity-item {
                    margin: 1px 0;
                }
                
                .capacity-item-header {
                    gap: 2px;
                }
                
                .fraction-label {
                    font-size: 6px;
                }
                
                .capacity-numbers {
                    font-size: 6px;
                }
                
                .capacity-item input {
                    font-size: 6px;
                    padding: 0px 1px;
                    width: 18px;
                    height: 12px;
                }
                
                /* Progress bar minimal */
                .capacity-progress {
                    height: 2px;
                    margin-top: 1px;
                }
            }
        `;
        document.head.appendChild(style);

        // Drucken
        window.print();

        // Style wieder entfernen
        setTimeout(() => {
            document.head.removeChild(style);
        }, 1000);
    }, []);

    const toggleTimeSlot = useCallback(async (zeitslotId: string, currentActive: boolean) => {
        console.log('[TOGGLE] Button clicked:', { zeitslotId, currentActive, selectedPlant });

        if (!zeitslotId) {
            console.error('[TOGGLE] No zeitslotId provided');
            return;
        }

        try {
            const requestBody = {
                zeitslotId,
                isActive: !currentActive,
                plant: selectedPlant,
            };

            console.log('[TOGGLE] Sending PATCH request:', { url: `${API_BASE_URL}/api/planning/toggle`, body: requestBody });

            const response = await fetch(
                `${API_BASE_URL}/api/planning/toggle`,
                {
                    method: "PATCH",
                    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify(requestBody),
                }
            );

            console.log('[TOGGLE] Response status:', response.status);

            if (!response.ok) {
                throw new Error("Fehler beim Umschalten");
            }

            setSaveMessage("✓ Status aktualisiert");
            setTimeout(() => setSaveMessage(null), 2000);

            // Reload data
            await fetchPlanningData();
        } catch (err) {
            console.error("Toggle error:", err);
            setSaveMessage("✗ Fehler beim Umschalten");
            setTimeout(() => setSaveMessage(null), 3000);
        }
    }, [selectedPlant, fetchPlanningData]);

    const updateCapacity = useCallback((
        day: string,
        timeSlot: string,
        fraction: string,
        newValue: number
    ) => {
        if (!planningData) return;

        console.log('[UPDATE_CAPACITY] Called with:', { day, timeSlot, fraction, newValue });

        // Debounce-Key für diese spezifische Fraktion
        const debounceKey = `${day}-${timeSlot}-${fraction}`;

        // Lösche vorherigen Timer falls vorhanden
        if (updateTimerRef.current[debounceKey]) {
            clearTimeout(updateTimerRef.current[debounceKey]);
            console.log('[UPDATE_CAPACITY] Cleared previous timer for:', debounceKey);
        }

        // Setze neuen Timer (1000ms Delay)
        updateTimerRef.current[debounceKey] = setTimeout(async () => {
            console.log('[UPDATE_CAPACITY] Sending to backend:', {
                day, timeSlot, fraction, newValue,
                url: `${API_BASE_URL}/api/planning/capacity/${day}/${timeSlot}/${fraction}`
            });

            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/planning/capacity/${day}/${timeSlot}/${fraction}`,
                    {
                        method: "PATCH",
                        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                            total: newValue,
                            calendarWeek: selectedWeek,
                            year: selectedYear,
                            plant: selectedPlant,
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Fehler beim Aktualisieren");
                }

                const result = await response.json();
                console.log('[UPDATE_CAPACITY] Backend response:', result);

                // Zeige Erfolgs-Feedback
                setSaveMessage("✓ Änderung gespeichert");
                setTimeout(() => setSaveMessage(null), 2000);

                // Reload data
                console.log('[UPDATE_CAPACITY] Refetching planning data...');
                await fetchPlanningData();

                // Lösche lokalen State für diese Fraktion nach erfolgreichem Save
                setLocalCapacities(prev => {
                    const newState = { ...prev };
                    delete newState[debounceKey];
                    console.log('[UPDATE_CAPACITY] Cleared local state for:', debounceKey, 'Remaining:', newState);
                    return newState;
                });
            } catch (err) {
                console.error('[UPDATE_CAPACITY] Error:', err);
                setSaveMessage("✗ Fehler beim Speichern");
                setTimeout(() => setSaveMessage(null), 3000);
            } finally {
                // Lösche Timer-Referenz
                delete updateTimerRef.current[debounceKey];
            }
        }, 1000);
    }, [planningData, selectedWeek, selectedYear, selectedPlant, fetchPlanningData]);

    if (loading && !planningData) {
        return (
            <div className="planning-overview">
                <div className="planning-loading">Lädt Planungsübersicht...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="planning-overview">
                <div className="planning-error">
                    <p>❌ Fehler: {error}</p>
                    <button onClick={() => void fetchPlanningData()}>Erneut versuchen</button>
                </div>
            </div>
        );
    }

    if (!planningData) {
        return <div className="planning-overview"></div>;
    }

    return (
        <div className="planning-overview">
            {saveMessage && (
                <div className={`save-notification ${saveMessage.includes('✓') ? 'success' : 'error'}`}>
                    {saveMessage}
                </div>
            )}

            <header className="planning-header">
                <h1>Planungsübersicht</h1>
                <div className="week-selector">
                    <select
                        value={selectedPlant}
                        onChange={handlePlantChange}
                        disabled={loading}
                        className="plant-select"
                    >
                        {PLANTS.map(plant => (
                            <option key={plant.id} value={plant.id}>
                                {plant.name}
                            </option>
                        ))}
                    </select>
                    <button onClick={handlePreviousWeek} disabled={loading}>←</button>
                    <span>KW {selectedWeek} / {selectedYear}</span>
                    <button onClick={handleNextWeek} disabled={loading}>→</button>
                    <button onClick={() => void fetchPlanningData()} disabled={loading} className="refresh-btn">
                        ↻ Aktualisieren
                    </button>
                    <button onClick={handlePrint} disabled={loading} className="print-btn" title="Drucken (Querformat)">
                        🖨️ Drucken
                    </button>
                </div>
            </header>

            <div className="planning-grid">
                <div className="grid-header">
                    <div className="header-cell time-slot-header">Zeitfenster</div>
                    {DAYS.map(day => (
                        <div key={day} className="header-cell day-header">{day}</div>
                    ))}
                </div>

                {TIME_SLOTS.map(slot => (
                    <div key={slot} className="grid-row">
                        <div className="time-slot-label">{slot} Uhr</div>

                        {DAYS.map(day => {
                            const slotData = planningData.overview[day]?.[slot];

                            if (!slotData) {
                                return <div key={day} className="time-slot-cell empty">-</div>;
                            }

                            // Berechne Gesamtsumme
                            const totalUsed = Object.values(slotData.capacities).reduce((sum, cap) => sum + cap.used, 0);
                            const totalCapacity = Object.values(slotData.capacities).reduce((sum, cap) => sum + cap.total, 0);
                            const isTotalOverloaded = totalUsed > totalCapacity;

                            return (
                                <div
                                    key={day}
                                    className={`time-slot-cell ${!slotData.isActive ? 'inactive' : ''}`}
                                >
                                    <div className="slot-header">
                                        <div className={`slot-total ${isTotalOverloaded ? 'total-overloaded' : ''}`}>
                                            {totalUsed} / {totalCapacity}
                                        </div>
                                        <button
                                            className="toggle-btn"
                                            onClick={() => {
                                                console.log('[TOGGLE] Button click event:', { id: slotData.id, isActive: slotData.isActive });
                                                if (slotData.id) {
                                                    toggleTimeSlot(slotData.id, slotData.isActive);
                                                }
                                            }}
                                            disabled={!slotData.id}
                                            title={slotData.isActive ? "Sperren" : "Freigeben"}
                                        >
                                            {slotData.isActive ? "🔓" : "🔒"}
                                        </button>
                                    </div>

                                    <div className="capacity-list">
                                        {Object.keys(slotData.capacities || {}).map((fraction: string) => {
                                            const cap = slotData.capacities[fraction];
                                            if (!cap) return null;

                                            const percentage = (cap.used / cap.total) * 100;
                                            const isOverloaded = cap.used > cap.total;

                                            return (
                                                <div key={fraction} className="capacity-item">
                                                    <div className="capacity-item-header">
                                                        <div className="fraction-label">{fraction}</div>
                                                        <div className="capacity-numbers">
                                                            <span className={isOverloaded ? 'text-danger' : ''}>
                                                                {cap.used}
                                                            </span>
                                                            {" / "}
                                                            <input
                                                                type="number"
                                                                className="capacity-input"
                                                                value={
                                                                    localCapacities[`${day}-${slot}-${fraction}`] !== undefined
                                                                        ? localCapacities[`${day}-${slot}-${fraction}`]
                                                                        : cap.total
                                                                }
                                                                onChange={(e) => {
                                                                    const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);

                                                                    const localKey = `${day}-${slot}-${fraction}`;

                                                                    console.log('[CAPACITY_UPDATE] Input changed:', {
                                                                        day, slot, fraction, value, localKey
                                                                    });

                                                                    // Update lokaler State sofort
                                                                    if (!isNaN(value) && value >= 0) {
                                                                        setLocalCapacities(prev => {
                                                                            const newState = { ...prev, [localKey]: value };
                                                                            console.log('[CAPACITY_UPDATE] New localCapacities:', newState);
                                                                            return newState;
                                                                        });
                                                                        void updateCapacity(day, slot, fraction, value);
                                                                    }
                                                                }}
                                                                min="0"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="capacity-bar">
                                                        <div
                                                            className={`capacity-fill ${isOverloaded ? 'overloaded' : ''}`}
                                                            style={{ width: `${Math.min(percentage, 100)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            <div className="planning-legend">
                <div className="legend-item">
                    <div className="legend-color" style={{ background: '#4caf50' }}></div>
                    <span>Belegt</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color" style={{ background: '#9e9e9e' }}></div>
                    <span>Verfügbar</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color" style={{ background: '#f44336' }}></div>
                    <span>Überbucht</span>
                </div>
                <div className="legend-item">
                    <span>🔒 = Gesperrt</span>
                </div>
            </div>
        </div>
    );
}
